import { useState, useCallback, useRef, useEffect } from 'react'
import { getUserId } from '@/features/site-pages/api'
import { checkSubdomainAvailability } from '../api'
import { showSimpleError, showSimpleSuccess } from '@/lib/toast'

const SUBDOMAIN_REGEX = /^[a-zA-Z0-9-]{4,100}$/

const validateSubdomainFormat = (value) => {
  if (!value) return null
  if (value.length < 4) return { status: 'error', message: 'Too short (minimum 4 characters)' }
  if (value.length > 100) return { status: 'error', message: 'Too long (maximum 100 characters)' }
  if (!SUBDOMAIN_REGEX.test(value)) return { status: 'error', message: 'Invalid characters (only letters, numbers, and dashes)' }
  return null
}

export const useSubdomainEditor = (projectSubdomain, isOpen) => {
  const [editor, setEditor] = useState(false)
  const [value, setValue] = useState('')
  const [validation, setValidation] = useState({ status: null, message: '' })
  const [isChecking, setIsChecking] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      setValue(projectSubdomain || '')
      setEditor(!projectSubdomain)
      setValidation({ status: null, message: '' })
    }
  }, [isOpen, projectSubdomain])

  useEffect(() => {
    if (editor) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editor])

  const debounceCheck = useCallback(
    (() => {
      let timeoutId
      return (val) => {
        clearTimeout(timeoutId)
        
        if (!val || val === projectSubdomain) {
          setValidation({ status: null, message: '' })
          setIsChecking(false)
          return
        }

        const formatError = validateSubdomainFormat(val)
        if (formatError) {
          setValidation(formatError)
          setIsChecking(false)
          return
        }

        setIsChecking(true)
        setValidation({ status: 'checking', message: 'Checking availability...' })
        
        timeoutId = setTimeout(async () => {
          try {
            const userId = getUserId()
            if (!userId) throw new Error('User ID not found')
            const result = await checkSubdomainAvailability(val, userId)
            const isAvailable = result?.error === false || result?.available === true
            const message = result?.message || result?.data?.message || (isAvailable ? 'Name available' : 'Name unavailable')

            setValidation({ status: isAvailable ? 'success' : 'error', message })
            isAvailable ? showSimpleSuccess(message) : showSimpleError(message)
          } catch (error) {
            const message = error.message || 'Failed to check subdomain'
            setValidation({ status: 'error', message })
            showSimpleError(message)
          } finally {
            setIsChecking(false)
          }
        }, 500)
      }
    })(),
    [projectSubdomain]
  )

  const handleChange = useCallback((e) => {
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
    setValue(val)
    debounceCheck(val)
  }, [debounceCheck])

  const handleFocus = useCallback(() => {
    inputRef.current?.select()
  }, [])

  const handleEdit = useCallback(() => {
    setEditor(true)
    setValue(projectSubdomain || '')
    setValidation({ status: null, message: '' })
    setTimeout(() => inputRef.current?.focus() || inputRef.current?.select(), 0)
  }, [projectSubdomain])

  const handleCancel = useCallback(() => {
    setEditor(false)
    setValue(projectSubdomain || '')
    setValidation({ status: null, message: '' })
  }, [projectSubdomain])

  return {
    editor,
    value,
    validation,
    isChecking,
    inputRef,
    setEditor,
    setValue,
    setValidation,
    handleChange,
    handleFocus,
    handleEdit,
    handleCancel,
  }
}

